import { Tabs, Tab } from '../../components';
import { ROUTE_QUERY_PARAM } from '../../app/Routes/const';
import { PERMISSIONS } from '../../shared/constants/permissions';
import {
  filterTabsByAllowedActions,
  type PermissionScopedTab,
  useTabPersistence,
} from '../../shared';
import { usePermissionAllowed } from '../../hooks/usePermissionAllowed';
import { DashboardTab } from './components/Dashboard';
import { CampaignTab } from './components/Campaign';
import { WidgetTab } from './components/Widget';
import { LatencyMetricsTab, ThroughputTab } from './components/LatencyMetrics';

type TabsEnum =
  | 'client'
  | 'campaign'
  | 'widget'
  | 'latency'
  | 'throughput';

const ANALYTICS_TABS: (PermissionScopedTab<TabsEnum> & { label: string })[] = [
  {
    name: 'client',
    label: 'Client',
    action: PERMISSIONS.VIEW_DASHBOARD_ANALYTICS,
  },
  {
    name: 'campaign',
    label: 'Campaign',
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    name: 'widget',
    label: 'Widget',
    action: PERMISSIONS.VIEW_WIDGET_ANALYTICS,
  },
  {
    name: 'latency',
    label: 'Latency Metrics',
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    name: 'throughput',
    label: 'Throughput',
    action: PERMISSIONS.SUPER_ADMIN,
  },
];

const AnalyticsPageWrapper = () => {
  const hasDashboardAnalytics = usePermissionAllowed([
    PERMISSIONS.VIEW_DASHBOARD_ANALYTICS,
  ]);
  const hasWidgetAnalytics = usePermissionAllowed([
    PERMISSIONS.VIEW_WIDGET_ANALYTICS,
  ]);
  const isSuperAdmin = usePermissionAllowed([PERMISSIONS.SUPER_ADMIN]);

  const availableTabs = filterTabsByAllowedActions(ANALYTICS_TABS, [
    ...(hasDashboardAnalytics ? [PERMISSIONS.VIEW_DASHBOARD_ANALYTICS] : []),
    ...(hasWidgetAnalytics ? [PERMISSIONS.VIEW_WIDGET_ANALYTICS] : []),
    ...(isSuperAdmin ? [PERMISSIONS.SUPER_ADMIN] : []),
  ]);

  const tabPersistenceConfig = {
    paramName: ROUTE_QUERY_PARAM.TAB,
    defaultTab: availableTabs[0] as TabsEnum,
    updateHistory: true,
  };

  const { selectedTab, setSelectedTab, isReady } = useTabPersistence(
    tabPersistenceConfig,
    availableTabs
  );

  if (availableTabs.length === 0) {
    return <div>No access</div>;
  }

  if (!isReady) {
    return <div>Loading...</div>;
  }

  if (availableTabs.length === 1) {
    const singleTab = availableTabs[0];
    if (singleTab === 'client') {
      return <DashboardTab />;
    }
    if (singleTab === 'widget') {
      return <WidgetTab />;
    }
    if (singleTab === 'campaign') {
      return <CampaignTab />;
    }
    if (singleTab === 'latency') {
      return <LatencyMetricsTab />;
    }
    if (singleTab === 'throughput') {
      return <ThroughputTab />;
    }
  }

  return (
    <>
      <Tabs<TabsEnum>
        tabs={ANALYTICS_TABS}
        activeTab={selectedTab}
        onChange={setSelectedTab}
        contentClassName="mt-md"
      >
        <Tab name="client">
          <DashboardTab />
        </Tab>
        <Tab name="campaign">
          <CampaignTab />
        </Tab>
        <Tab name="widget">
          <WidgetTab />
        </Tab>
        <Tab name="latency">
          <LatencyMetricsTab />
        </Tab>
        <Tab name="throughput">
          <ThroughputTab />
        </Tab>
      </Tabs>
    </>
  );
};

export default AnalyticsPageWrapper;
